import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ClientAppShell from "../../components/ClientAppShell";
import ProfileInfo from "../components/profile-info";
import ContentLoader from "../../components/ContentLoader";
import SmeAssessTab from "../../components/sme/SmeAssessTab";
import SmeChatTab from "../../components/sme/SmeChatTab";
import { getSMEPendingProjectList } from "../../services/smeApi";
import { fetchChat } from "../../services/chatApi";
import { useAuth } from "../../auth/AuthContext";
import {
  dedupePendingProjects,
  getClientUserId,
  getCompanyName,
  getPendingProjectKey,
  getProjectId,
  readCachedSmePendingProjects,
  writeCachedSmePendingProjects,
} from "../../utils/smeProjectUtils";
import ProjectSelectionScreen from "../../components/ProjectSelectionScreen";
import SmeProjectWorkspace, {
  type SmeWorkspaceTab,
} from "../../components/sme/SmeProjectWorkspace";
import { Alert } from "../../components/ui";
import {
  CHAT_READ_STATE_CHANGED,
  hasUnreadChatNotification,
} from "../../utils/chatReadState";
import "../../styles/sme-workspace.css";

const SMETracking = () => {
  const { user } = useAuth();
  const [serverError, setServerError] = useState("");
  const [profile, setProfile] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [selectedTab, setSelectedTab] = useState<SmeWorkspaceTab>("assessment");
  const [mountedWorkspaceTabs, setMountedWorkspaceTabs] = useState(
    () => new Set<SmeWorkspaceTab>(["assessment"])
  );
  const [sidebarChatSnapshot, setSidebarChatSnapshot] = useState([]);

  const [pendingProjects, setPendingProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [hasUnreadChat, setHasUnreadChat] = useState(false);
  const chatHistoryRef = useRef([]);

  const clientUserId = useMemo(
    () => getClientUserId(selectedProject),
    [selectedProject]
  );
  const selectedProjectId = useMemo(
    () => getProjectId(selectedProject),
    [selectedProject]
  );
  const assessUser = useMemo(
    () =>
      selectedProject
        ? {
            project: selectedProject,
            userId: clientUserId,
          }
        : null,
    [selectedProject, clientUserId]
  );

  const recomputeUnreadChat = useCallback(() => {
    if (!clientUserId || !selectedProjectId) {
      setHasUnreadChat(false);
      return;
    }

    setHasUnreadChat(
      hasUnreadChatNotification(
        chatHistoryRef.current,
        clientUserId,
        selectedProjectId,
        "sme"
      )
    );
  }, [clientUserId, selectedProjectId]);

  const fetchUnreadChatStatus = useCallback(async () => {
    if (!clientUserId || !selectedProjectId) {
      chatHistoryRef.current = [];
      setHasUnreadChat(false);
      return;
    }

    try {
      const chatHistory = await fetchChat({
        userId: clientUserId,
        projectId: selectedProjectId,
      });
      const normalized = Array.isArray(chatHistory) ? chatHistory : [];
      chatHistoryRef.current = normalized;
      setSidebarChatSnapshot(normalized);
      recomputeUnreadChat();
    } catch (error) {
      console.error("Error checking unread chat status:", error);
    }
  }, [clientUserId, selectedProjectId, recomputeUnreadChat]);

  const handleMenuSelection = (item) => {
    setSelectedMenuItem(item);
  };

  useEffect(() => {
    document.body.classList.remove("modal-open");
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  useEffect(() => {
    fetchUnreadChatStatus();
  }, [fetchUnreadChatStatus]);

  useEffect(() => {
    if (!selectedProjectId || !clientUserId) {
      return undefined;
    }

    const interval = setInterval(() => {
      fetchUnreadChatStatus();
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchUnreadChatStatus, selectedProjectId, clientUserId]);

  useEffect(() => {
    const handleChatReadStateChanged = () => {
      recomputeUnreadChat();
    };

    window.addEventListener(CHAT_READ_STATE_CHANGED, handleChatReadStateChanged);
    return () => {
      window.removeEventListener(
        CHAT_READ_STATE_CHANGED,
        handleChatReadStateChanged
      );
    };
  }, [recomputeUnreadChat]);

  useEffect(() => {
    const fetchPendingProjectsAndUserId = async () => {
      const cached = readCachedSmePendingProjects();
      if (cached) {
        setPendingProjects(dedupePendingProjects(cached));
        setLoadingProjects(false);
        if (user) {
          setProfile(user);
        }
      } else {
        setLoadingProjects(true);
      }

      try {
        const result = await getSMEPendingProjectList();
        const projects = dedupePendingProjects(
          result?.data?.projects ?? result?.projects ?? []
        );

        setPendingProjects(projects);
        writeCachedSmePendingProjects(
          result?.data?.projects ?? result?.projects ?? projects
        );

        if (user) {
          setProfile(user);
        }
      } catch (error) {
        console.error("Error fetching pending projects:", error);
        setServerError("Error fetching pending projects: " + error.message);

        if (user) {
          setProfile(user);
        }
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchPendingProjectsAndUserId();
  }, [user]);

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setSelectedTab("assessment");
    setMountedWorkspaceTabs(new Set(["assessment"]));
    setHasUnreadChat(false);
    chatHistoryRef.current = [];
    setSidebarChatSnapshot([]);
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setHasUnreadChat(false);
    chatHistoryRef.current = [];
  };

  const handleTabClick = (tab: SmeWorkspaceTab) => {
    if (tab === selectedTab) return;
    setMountedWorkspaceTabs((prev) => {
      if (prev.has(tab)) return prev;
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
    setSelectedTab(tab);
  };

  const renderProjectMeta = (project) => {
    const rows = [
      project.username && { label: "User", value: project.username },
      getCompanyName(project) && { label: "Company", value: getCompanyName(project) },
      project.departmentName && { label: "Department", value: project.departmentName },
      project.designation && { label: "Designation", value: project.designation },
    ].filter(Boolean);

    return rows;
  };

  const renderProjectCards = () => {
    if (loadingProjects) {
      return <ContentLoader compact label="Loading projects…" />;
    }

    const projects = pendingProjects.map((project) => ({
      id: getPendingProjectKey(project),
      name: project.projectName || "Untitled project",
      companyName: getCompanyName(project) || null,
      meta: renderProjectMeta(project).filter(
        (row) => row.label !== "Company"
      ),
    }));

    return (
      <ProjectSelectionScreen
        embedded
        pickerHeading="Your projects"
        title="Your projects"
        eyebrow={null}
        subtitle={null}
        projects={projects}
        onSelect={(id) => {
          const project = pendingProjects.find(
            (item) => getPendingProjectKey(item) === String(id)
          );
          if (project) {
            handleProjectSelect(project);
          }
        }}
        emptyTitle="No pending projects"
        emptyHint={
          user?.userId
            ? "There are no assessments awaiting your review right now."
            : "Projects will appear here once clients submit assessments."
        }
      />
    );
  };

  const renderTabsContent = () => {
    const workspaceMeta = [
      getCompanyName(selectedProject),
      selectedProject?.username,
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <SmeProjectWorkspace
        projectName={selectedProject?.projectName || "Selected project"}
        projectMeta={workspaceMeta || undefined}
        activeTab={selectedTab}
        hasUnreadChat={hasUnreadChat}
        onTabChange={handleTabClick}
        onBack={handleBackToProjects}
        assessmentPanel={
          <SmeAssessTab
            assessUser={assessUser}
            projectId={selectedProjectId}
            clientUserId={clientUserId}
            isTabActive={selectedTab === "assessment"}
          />
        }
        chatPanel={
          mountedWorkspaceTabs.has("chat") ? (
            <SmeChatTab
              project={selectedProject}
              clientUserId={clientUserId}
              projectId={selectedProjectId}
              isTabActive={selectedTab === "chat"}
              prefetchedChatHistory={sidebarChatSnapshot}
            />
          ) : (
            <ContentLoader compact label="Open Chat to load the conversation…" />
          )
        }
        footer={
          serverError ? (
            <Alert variant="error" description={serverError} />
          ) : null
        }
      />
    );
  };

  return (
    <ClientAppShell
      title="Assessment Tracking"
      dataNav="assessment-tracking"
      contentClassName="sme-workspace-page"
      selectedItem={selectedMenuItem}
      onMenuSelect={handleMenuSelection}
      profileSlot={
        <ProfileInfo profile={profile} currentUserId={user?.userId} />
      }
      intro={
        !selectedProject
          ? {
              eyebrow: "Expert review workspace",
              description:
                "Select a client project to review assessment responses and chat with the team.",
            }
          : undefined
      }
    >
      {!selectedProject ? renderProjectCards() : renderTabsContent()}
    </ClientAppShell>
  );
};

export default SMETracking;
